import Env from '@ridango/core/env';
import { ProductPropertiesType } from '@ridango/core';
import { AppStartupService } from '@ridango/core/lib/services/app-startup.service';
import {
    useIntlPeriodTicketValidityDisplay,
    useUnifiedTicketValidityDisplay
} from '@ridango/core/lib/utils/ticket-validity-display.helper';
import { FormattingContext } from '@ridango/core/lib/enums/ridango-date-time-pipe-context.enum';
import { RidangoDateTimeOptions } from '@ridango/core/lib/models/ridango-date-time-pipe.interface';

export { useIntlPeriodTicketValidityDisplay, useUnifiedTicketValidityDisplay };

type TicketProductTypeSource = {
    product_type?: string;
    product?: { product_type?: string } | null;
};

type TicketWithDuration = TicketProductTypeSource & {
    starting?: string | Date;
    ending?: string | Date;
};

type TicketProductProperty = {
    code?: string;
    value?: unknown;
};

type TicketWithProductProperties = TicketProductTypeSource & {
    product?: { product_properties?: TicketProductProperty[]; product_type?: string } | null;
    product_object?: { product_properties?: TicketProductProperty[] } | null;
};

export type FixedValidityRange = {
    start: Date;
    end: Date;
    startCode: string;
    endCode: string;
};

export type FixedValidityRangeOptions = {
    applyTransportationDayOffset?: boolean;
    transportationDayOffsetMinutes?: number;
};

export function fixedValidityRangeOptionsFromAppStartup(
    appStartupService: Pick<AppStartupService, 'isTransportationDayOffsetEnabled' | 'getTransportationDayOffset'>
): FixedValidityRangeOptions {
    return {
        applyTransportationDayOffset: appStartupService.isTransportationDayOffsetEnabled(),
        transportationDayOffsetMinutes: appStartupService.getTransportationDayOffset().total('minutes')
    };
}

/**
 * Determines if ticket should display full datetime format instead of date-only format.
 * M (minute-based) and H (hour-based) tickets show time, other types show only date.
 * Falls back to duration check if product_type is unavailable.
 */
export function shouldUseDateTimeForQrTicket(ticket: TicketWithDuration | null | undefined): boolean {
    if (!ticket) {
        return false;
    }

    // Check product_type if available (from enrichment or API)
    const productType = ticket.product_type || ticket.product?.product_type;
    if (productType === 'M' || productType === 'H') {
        return true;
    }

    // Fallback: Check ticket duration (≤24 hours = show time)
    if (ticket.starting && ticket.ending) {
        const start = new Date(ticket.starting).getTime();
        const end = new Date(ticket.ending).getTime();
        if (Number.isNaN(start) || Number.isNaN(end)) {
            return false;
        }

        const durationHours = (end - start) / (1000 * 60 * 60);
        return durationHours > 0 && durationHours <= 24;
    }

    return false;
}

export function ridangoDateTimeValidityOptions(
    env: Env,
    ticket?: TicketProductTypeSource | null,
    context: RidangoDateTimeOptions['context'] = FormattingContext.PERIOD_TICKET_VALIDITY
): RidangoDateTimeOptions {
    return {
        format: env.PERIOD_TICKET_DATE_FORMAT,
        formatOptions: {
            ...env.FORMATTING?.PERIOD_TICKET_VALIDITY,
            rangeSeparator: env.FORMATTING?.TICKET_VALIDITY_RANGE_SEPARATOR ?? ' - '
        },
        context,
        applyTransportDayOffset: !!env.FORMATTING?.USE_TRANSPORTATION_DAY_OFFSET,
        productType: ticket?.product_type ?? ticket?.product?.product_type ?? null
    };
}

export function resolveFixedValidityRange(
    ticket: TicketWithProductProperties | null | undefined,
    options?: FixedValidityRangeOptions
): FixedValidityRange | null {
    if (!ticket) {
        return null;
    }

    const properties = ticket.product?.product_properties || ticket.product_object?.product_properties || [];

    if (!properties?.length) {
        return null;
    }

    const hasFixedStartAndEnd = properties.some((prop) => prop?.code === ProductPropertiesType.FixedStartAndEndDate);
    if (!hasFixedStartAndEnd) {
        return null;
    }

    const startCode = properties.find((prop) => prop?.code === ProductPropertiesType.TicketStartDate)?.value;
    const endCode = properties.find((prop) => prop?.code === ProductPropertiesType.TicketEndDate)?.value;

    if (typeof startCode !== 'string' || typeof endCode !== 'string') {
        return null;
    }

    // Product property values are MM-DD and should be treated as absolute local dates.
    const startParts = parseMonthDay(startCode);
    const endParts = parseMonthDay(endCode);
    if (!startParts || !endParts) {
        return null;
    }

    const { day: startDay, month: startMonth } = startParts;
    const { day: endDay, month: endMonth } = endParts;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const currentDay = now.getDate();

    const { startYear, endYear } = resolveFixedRangeYears({
        currentYear,
        currentMonth,
        currentDay,
        startMonth,
        startDay,
        endMonth,
        endDay
    });

    const { start, end } = buildFixedRangeDates(
        { startYear, startMonth, startDay, endYear, endMonth, endDay },
        options
    );

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
    }

    return {
        start,
        end,
        startCode,
        endCode
    };
}

function buildFixedRangeDates(
    input: {
        startYear: number;
        startMonth: number;
        startDay: number;
        endYear: number;
        endMonth: number;
        endDay: number;
    },
    options?: FixedValidityRangeOptions
): { start: Date; end: Date } {
    const { startYear, startMonth, startDay, endYear, endMonth, endDay } = input;

    const startBase = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
    const endBase = new Date(endYear, endMonth - 1, endDay, 23, 59, 0, 0);

    if (!options?.applyTransportationDayOffset) {
        return { start: startBase, end: endBase };
    }

    const offsetMinutes = Math.max(0, Math.floor(options.transportationDayOffsetMinutes ?? 0));
    if (offsetMinutes <= 0) {
        return { start: startBase, end: endBase };
    }

    const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
    start.setMinutes(start.getMinutes() + offsetMinutes);

    // END_DATE marks the transportation day itself, so the visible end moment is
    // the next calendar day at offset-1 minute (e.g. 08-25 with 240 -> 26.08 03:59).
    const end = new Date(endYear, endMonth - 1, endDay + 1, 0, 0, 0, 0);
    end.setMinutes(end.getMinutes() + offsetMinutes - 1);

    return { start, end };
}

function parseMonthDay(value: string): { day: number; month: number } | null {
    const parts = value.split('-').map(Number);
    if (parts.length !== 2) {
        return null;
    }

    const [month, day] = parts;
    if (!Number.isFinite(day) || !Number.isFinite(month)) {
        return null;
    }

    if (day < 1 || day > 31 || month < 1 || month > 12) {
        return null;
    }

    return { day, month };
}

function resolveFixedRangeYears(input: {
    currentYear: number;
    currentMonth: number;
    currentDay: number;
    startMonth: number;
    startDay: number;
    endMonth: number;
    endDay: number;
}): { startYear: number; endYear: number } {
    const { currentYear, currentMonth, currentDay, startMonth, startDay, endMonth, endDay } = input;

    const isCrossYear = startMonth > endMonth;
    if (isCrossYear) {
        // Current date is on/after fixed end date, so next season ends next year.
        const afterEnd = currentMonth > endMonth || (currentMonth === endMonth && currentDay >= endDay);
        if (afterEnd) {
            return { startYear: currentYear, endYear: currentYear + 1 };
        }

        // Current date is before the fixed start date, so current season started last year.
        const beforeStart = currentMonth < startMonth || (currentMonth === startMonth && currentDay < startDay);
        if (beforeStart) {
            return { startYear: currentYear - 1, endYear: currentYear };
        }

        // Current date is inside the active cross-year window.
        return { startYear: currentYear, endYear: currentYear + 1 };
    }

    const afterSeasonEnd = currentMonth > endMonth || (currentMonth === endMonth && currentDay > endDay);

    // Same-year ranges stay in current year until season end, then roll to next season year.
    if (afterSeasonEnd) {
        return { startYear: currentYear + 1, endYear: currentYear + 1 };
    }

    return { startYear: currentYear, endYear: currentYear };
}
